/**
 * Policies: a list, then one policy's own view. The list says what each policy covers; a policy's
 * view is its rules as one list of sections, the document last, and a floating bar while there are
 * changes to publish. Publishing asks first, with what the machines in scope would make of it.
 * Drafts live in sessionStorage, so a sign-in renewal never loses them. "Workbench settings" sits
 * beside the list: settings every machine starts from, whatever policy a project has. "Exceptions" is the third: what
 * people asked for where the policy refused them (workbench D379), answered here.
 */

import { keepUnknownPolicy, workspacePolicySchema, type WorkspacePolicy } from '@aioe/shared';
import { ArrowLeft, Display, FileText, Folder, Shield, ShieldExclamation, TriangleExclamation } from '@gravity-ui/icons';
import { Input, TextField, Tooltip } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { ExceptionRequests, useExceptionRequests } from '../components/exceptionRequests.tsx';
import { ManifestClauseSections } from '../components/manifestClauses.tsx';
import { CompatibilityReport, oldestVersion, refusingCount, usePolicyCheck } from '../components/policyCompatibility.tsx';
import { PolicyEditor } from '../components/policyEditor.tsx';
import { DeclarationsCheck, SettingsPolicyEditor } from '../components/settingsPolicy.tsx';
import { Button, ConfirmDialog, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, SaveBar, SectionHeading, Stat, StatusMark } from '../components/ui.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { timeAgo, useDraft, useLoad } from '../hooks.ts';
import { useNames } from '../names.ts';
import { ProjectChips, ProjectPicker } from '../components/ProjectPicker.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.policies!;

// The starting document shows every block, including the optional agents (sandbox) and network
// (egress) blocks and the DLP classifier's node, so an administrator sees what can be set.
const STARTER = JSON.stringify(workspacePolicySchema.parse({ dlp: { classifier: { node: undefined, tags: [] } }, agents: {}, network: {} }), null, 2);

type Published = { id: string; name: string; projectIds: string[]; revision: string; policy: WorkspacePolicy; updatedAt: string };
type ProjectName = { id: string; name: string };

/** The document as an object, or an empty one while it is not valid JSON. */
function parseOr(json: string): Record<string, unknown> {
  try {
    const v: unknown = JSON.parse(json);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** "Billing, Payments, and 2 more"; the organisation default covers what no other policy names. */
function appliesTo(projectIds: string[], projects: ProjectName[]): string {
  if (!projectIds.length) return 'Every project without a policy of its own';
  const names = projectIds.map((id) => projects.find((p) => p.id === id)?.name ?? id);
  const shown = names.slice(0, 2).join(', ');
  return names.length > 2 ? `${shown}, and ${names.length - 2} more` : shown;
}

/** An identifier from a name: lower case, hyphens, and unique among the published ones. */
function identifierFor(name: string, taken: Set<string>): string {
  const base =
    name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'policy';
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  return id;
}

export function PoliciesPage() {
  const { policyId } = useParams();
  return policyId ? <PolicyPage id={policyId} /> : <PolicyList />;
}

/* ---------- the list ---------- */

function PolicyList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view');
  const tab = view === 'settings' || view === 'exceptions' ? view : 'policies';
  const exceptions = useExceptionRequests();
  const waiting = exceptions.data ? exceptions.data.requests.filter((r) => r.state === 'pending').length : undefined;
  const policies = useLoad(() => routes.policies(), []);
  // Counts, not lists: one project and one machine are asked for, and the totals come with them.
  const projectCount = useLoad(() => routes.projectsPage({ limit: 1 }).then((a) => a.extra.total).catch(() => undefined), []);
  const machineCount = useLoad(() => routes.devicesPage({ limit: 1 }).then((a) => a.extra.total).catch(() => undefined), []);
  const governed = useLoad(() => routes.settingsPolicy(), []);
  const manifest = useLoad(() => routes.currentPolicyManifest().catch(() => undefined), []);
  // What the machines make of the policies as published: the ones that would refuse one keep their last.
  const checks = useLoad(
    () =>
      Promise.all((policies.data ?? []).map((p) => routes.checkPolicy({ policy: p.policy as unknown as Record<string, unknown>, ...(p.projectIds.length ? { projectIds: p.projectIds } : {}) }).catch(() => undefined))),
    [policies.data?.map((p) => `${p.id}@${p.revision}`).join(',') ?? ''],
  );
  const list = [...(policies.data ?? [])].sort((a, b) => (a.projectIds.length === 0 ? -1 : b.projectIds.length === 0 ? 1 : a.name.localeCompare(b.name)));
  const hasDefault = list.some((p) => p.projectIds.length === 0);
  const covered = new Set(list.flatMap((p) => p.projectIds)).size;
  const refusing = new Set((checks.data ?? []).flatMap((a) => (a ? a.digests.filter((d) => d.errors.length).flatMap((d) => d.devices.map((x) => x.deviceId)) : []))).size;
  const namedMap = useNames(list.flatMap((p) => p.projectIds)).projects;
  const namedProjects = [...namedMap].map(([id, name]) => ({ id, name }));
  const settingsCount = governed.data ? Object.keys(governed.data.settings).length : undefined;

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Shield />}
        title="Policies"
        subtitle="The rules workbenches follow for each project: which models, what data may leave, who approves what."
        actions={
          tab === 'policies' && list.length ? (
            <Button size="sm" variant="primary" onPress={() => navigate('/policies/new')}>
              New policy
            </Button>
          ) : undefined
        }
      />
      <Explainer id="policies" tone={tone} icon={<Shield />} title="How policies work" summary="One default for the organisation, and a policy of its own for any project that needs one">
        <p>A policy is the set of rules a workbench follows while it works on a project: which runtimes and models it may use, what personal or secret information may leave, which actions a person approves first, and what a remote device may do.</p>
        <p>The organisation default covers every project without a policy of its own. Machines pick a change up at their next check-in; before you publish, the console asks the machines in scope what they would make of it, and one that would refuse keeps its last policy.</p>
        <p>Workbench settings are what every machine starts from, whatever its project’s policy. Exceptions are what people asked for where a policy stopped them.</p>
      </Explainer>
      <div className="art-stats">
        <Stat tone={tone} icon={<Shield />} label="Policies" value={policies.data?.length} detail={hasDefault ? 'One is the organisation default' : 'No organisation default yet'} />
        <Stat tone={tone} icon={<Folder />} label="Projects with their own" value={policies.data ? covered : undefined} detail={projectCount.data !== undefined ? `Of ${projectCount.data} ${projectCount.data === 1 ? 'project' : 'projects'}` : ''} />
        <Stat tone={tone} icon={<Display />} label="Machines" value={machineCount.data} detail="Follow these policies" />
        <Stat
          tone={tone}
          icon={<TriangleExclamation />}
          label="Would refuse"
          value={checks.data ? refusing : undefined}
          detail={refusing ? 'Machines keeping their last policy' : 'Every machine applies its policy'}
          needsYou={refusing > 0}
        />
        <Stat
          tone={tone}
          icon={<ShieldExclamation />}
          label="Exceptions"
          value={waiting}
          detail={waiting ? 'Waiting on an answer' : 'Nobody is waiting'}
          needsYou={Boolean(waiting)}
          onPress={() => setParams({ view: 'exceptions' }, { replace: true })}
          selected={tab === 'exceptions'}
        />
      </div>
      <Segment aria-label="Policies view" size="sm" selectedKey={tab} onSelectionChange={(k) => setParams(k === 'settings' || k === 'exceptions' ? { view: String(k) } : {}, { replace: true })} className="w-fit max-w-full">
        <Segment.Item id="policies">Policies</Segment.Item>
        <Segment.Item id="settings">Workbench settings{settingsCount ? ` · ${settingsCount}` : ''}</Segment.Item>
        <Segment.Item id="exceptions">Exceptions{waiting ? ` · ${waiting}` : ''}</Segment.Item>
      </Segment>
      <ErrorNote error={policies.error} />
      <PartBoundary key="policies" name="Published policies" tone={tone}>
        {tab === 'policies' && (
          <ListGroup
            title="Published"
            count={policies.data?.length}
            description="The default first, then each project’s own. Open one to read or change its rules."
            empty={
              policies.data && !list.length ? (
                <Empty
                  tone={tone}
                  icon={<Shield />}
                  title="No policy yet"
                  body="A policy is a set of rules a workbench follows for a project: which models, what data may leave, who approves what. Start with the organisation default."
                  action={
                    <Button size="sm" variant="primary" onPress={() => navigate('/policies/new')}>
                      Write the organisation default
                    </Button>
                  }
                />
              ) : undefined
            }
          >
            {!policies.data ? <ListBlock className="text-muted">Reading the policies…</ListBlock> : null}
            {list.map((p) => {
              const refused = refusingCount(checks.data?.[(policies.data ?? []).indexOf(p)]);
              return (
                <ListRow
                  key={p.id}
                  to={`/policies/${encodeURIComponent(p.id)}`}
                  icon={p.projectIds.length ? <Folder /> : <Shield />}
                  title={p.name}
                  description={
                    <>
                      <span className="block">{p.projectIds.length ? `Applies to ${appliesTo(p.projectIds, namedProjects)}` : 'The organisation default: every project without a policy of its own'}</span>
                      <span className="block tabular-nums">Revision {p.revision} · updated {timeAgo(p.updatedAt)}</span>
                    </>
                  }
                  status={refused ? <StatusMark state="error" label={`${refused} would refuse`} /> : undefined}
                />
              );
            })}
          </ListGroup>
        )}
      </PartBoundary>
      <PartBoundary key="settings" name="Workbench settings" tone={tone}>
        {tab === 'settings' && governed.data && (
          <>
            {manifest.data ? <p className="px-0.5 text-xs text-muted">Settings and their groups come from AI Workbench {manifest.data.manifest.version}, the newest build your machines run.</p> : <DeclarationsCheck />}
            <SettingsPolicyEditor
              key={`${JSON.stringify(governed.data)}-${manifest.data?.digest ?? ''}`}
              value={governed.data}
              manifest={manifest.data?.manifest}
              canEdit
              draftKey="organisation.settings"
              title="Workbench settings"
              hint="Settings every machine starts from, whatever policy a project has. Suggested: people may change it. Locked: they cannot."
              onSave={async (p) => {
                await routes.saveSettingsPolicy(p);
                governed.reload();
              }}
            />
          </>
        )}
      </PartBoundary>
      {tab === 'settings' && <ErrorNote error={governed.error} />}
      <PartBoundary key="exceptions" name="Exception requests" tone={tone}>
        {tab === 'exceptions' && <ExceptionRequests list={exceptions} />}
      </PartBoundary>
    </PageColumn>
  );
}

/* ---------- one policy ---------- */

interface Draft {
  name: string;
  projectIds: string[];
  json: string;
}

function PolicyPage({ id }: { id: string }) {
  const policies = useLoad(() => routes.policies(), []);
  const manifest = useLoad(() => routes.currentPolicyManifest().catch(() => undefined), []);
  const isNew = id === 'new';
  if (policies.error) {
    return (
      <PageColumn width="form">
        <ErrorNote error={policies.error} />
      </PageColumn>
    );
  }
  if (!policies.data) return null;
  const published = isNew ? undefined : policies.data.find((p) => p.id === id);
  if (!isNew && !published) {
    return (
      <PageColumn width="form">
        <PageHeader tone={tone} icon={<Shield />} title="Policy not found" subtitle="It may have been removed." />
        <Empty
          tone={tone}
          icon={<Shield />}
          title="No policy by that name"
          body="It may have been removed, or the address is not quite right."
          action={
            <Link to="/policies" className="button button--secondary button--sm">
              All policies
            </Link>
          }
        />
      </PageColumn>
    );
  }
  const hasDefault = policies.data.some((p) => p.projectIds.length === 0);
  const original: Draft = published
    ? { name: published.name, projectIds: published.projectIds, json: JSON.stringify(published.policy, null, 2) }
    : { name: hasDefault ? '' : 'Organisation default', projectIds: [], json: STARTER };
  return (
    <PolicyEditorView
      key={published ? `${published.id}@${published.revision}` : 'new'}
      published={published}
      original={original}
      all={policies.data}
      manifest={manifest.data?.manifest}
      onPublished={() => policies.reload()}
    />
  );
}

function PolicyEditorView({ published, original, all, manifest, onPublished }: { published?: Published; original: Draft; all: Published[]; manifest?: Parameters<typeof ManifestClauseSections>[0]['manifest']; onPublished: () => void }) {
  const navigate = useNavigate();
  const [draft, setDraft, discard] = useDraft<Draft>(`policy.${published?.id ?? 'new'}`, original);
  const [confirming, setConfirming] = useState<'publish' | 'remove'>();
  const [error, setError] = useState<string>();
  const { answer, error: checkError } = usePolicyCheck(draft.json, draft.projectIds);
  const hasDefault = all.some((p) => p.projectIds.length === 0 && p.id !== published?.id);
  // The identifier comes from the name on creation (the first policy is "default"), and never changes after.
  const target = published?.id ?? (hasDefault || draft.projectIds.length ? identifierFor(draft.name, new Set(all.map((p) => p.id))) : 'default');
  const changes = countChanges(original, draft);
  const dirty = !published || changes > 0;
  const refusing = refusingCount(answer);
  const replacing = !published ? all.find((p) => p.id === target) : undefined;

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  function validate(): WorkspacePolicy | undefined {
    let policy: unknown;
    try {
      policy = JSON.parse(draft.json);
    } catch (e) {
      setError(`The document is not valid JSON: ${(e as Error).message}`);
      return undefined;
    }
    const parsed = workspacePolicySchema.safeParse(policy);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return undefined;
    }
    return keepUnknownPolicy(policy, parsed.data);
  }

  async function publish() {
    const policy = validate();
    if (!policy) return setConfirming(undefined);
    try {
      await routes.savePolicy(target, { name: draft.name.trim(), projectIds: draft.projectIds, policy });
      discard();
      setConfirming(undefined);
      onPublished();
      if (!published) navigate(`/policies/${encodeURIComponent(target)}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setConfirming(undefined);
    }
  }

  async function remove() {
    if (!published) return;
    try {
      await routes.deletePolicy(published.id);
      discard();
      navigate('/policies');
    } catch (e) {
      setError((e as Error).message);
      setConfirming(undefined);
    }
  }

  const title = published ? draft.name.trim() || published.name : draft.name.trim() || 'New Policy';
  // Once there is a default, a policy with no project ticked is not the default: it covers nothing yet.
  const isDefault = !draft.projectIds.length && !hasDefault;
  const chosen = useNames(draft.projectIds).projects;
  const scope = !draft.projectIds.length && hasDefault ? 'no project yet; choose the ones it is for' : appliesTo(draft.projectIds, [...chosen].map(([id, name]) => ({ id, name })));
  return (
    <PageColumn width="form">
      <PageHeader
        tone={tone}
        icon={<Shield />}
        title={title}
        subtitle={`Applies to ${scope.replace(/^Every/, 'every')}`}
        actions={
          <>
            <Button size="sm" variant="ghost" onPress={() => navigate('/policies')}>
              <ArrowLeft className="size-4" />
              All policies
            </Button>
            {published && <RowMenu label={`More for ${published.name}`} items={[{ id: 'remove', label: 'Remove this policy…', danger: true }]} onAction={() => setConfirming('remove')} />}
          </>
        }
      />
      <PartBoundary key="editor" name="Policy editor" tone={tone}>
      <div className="art-stats">
        <Stat tone={tone} icon={<Folder />} label="Projects" value={isDefault ? 'All' : draft.projectIds.length} detail={isDefault ? 'Without a policy of their own' : 'Named by this policy'} />
        <Stat tone={tone} icon={<Display />} label="Machines in scope" value={answer?.devices} detail="Would receive it" />
        <Stat tone={tone} icon={<TriangleExclamation />} label="Would refuse" value={answer ? refusing : undefined} detail={refusing ? 'They keep their last policy' : 'Every machine applies it'} needsYou={refusing > 0} />
        <Stat tone={tone} icon={<FileText />} label="Revision" value={published?.revision} detail={published ? `Updated ${timeAgo(published.updatedAt)}` : 'Not published yet'} />
      </div>

      <ListGroup title="About this policy">
        <ListRow
          title="Name"
          htmlFor="policy-name"
          description={
            published ? (
              <>
                Workbenches know it as{' '}
                <Tooltip delay={300}>
                  <Tooltip.Trigger className="cursor-default underline decoration-dotted underline-offset-4" tabIndex={0}>
                    its identifier
                  </Tooltip.Trigger>
                  <Tooltip.Content>{published.id}</Tooltip.Content>
                </Tooltip>
                , which stays the same when it is renamed.
              </>
            ) : (
              'What people see it called. Its identifier comes from this name and never changes.'
            )
          }
          control={
            <TextField variant="secondary" aria-label="Name" value={draft.name} onChange={(name) => set({ name })} isRequired className="w-72 max-w-full">
              <Input id="policy-name" placeholder={hasDefault ? 'Payments projects' : 'Organisation default'} />
            </TextField>
          }
        />
        <AppliesTo value={draft.projectIds} onChange={(projectIds) => set({ projectIds })} defaultTaken={hasDefault} />
      </ListGroup>

      <section className="grid gap-2">
        <SectionHeading title="Rules" description="Each section says what it does now. Open one to change it." />
        <PolicyEditor
          json={draft.json}
          onChange={(json) => set({ json })}
          more={manifest ? <ManifestClauseSections manifest={manifest} raw={parseOr(draft.json)} onChange={(next) => set({ json: JSON.stringify(next, null, 2) })} oldestVersion={oldestVersion(answer)} /> : null}
        />
      </section>
      <ErrorNote error={error} />

      <SaveBar
        isOpen={dirty}
        label={published ? `${changes} ${changes === 1 ? 'change' : 'changes'}` : 'Not published yet'}
        onDiscard={() => {
          discard();
          setError(undefined);
          if (!published) navigate('/policies');
        }}
        onSave={() => {
          setError(undefined);
          if (validate()) setConfirming('publish');
        }}
        saveLabel="Publish"
        isDisabled={!draft.name.trim()}
      />
      </PartBoundary>

      <ConfirmDialog isOpen={confirming === 'publish'} title={`Publish ${draft.name.trim() || 'this policy'}?`} confirmLabel="Publish" danger={refusing > 0 || Boolean(replacing)} onConfirm={publish} onCancel={() => setConfirming(undefined)}>
        <p>{answer?.devices ? `It goes to ${answer.devices} ${answer.devices === 1 ? 'machine' : 'machines'} at their next check-in.` : 'It goes to the machines in its scope at their next check-in.'}</p>
        {replacing ? <p className="text-danger">It replaces {replacing.name}, which has the same identifier.</p> : null}
        <CompatibilityReport answer={answer} error={checkError} manifest={manifest} />
      </ConfirmDialog>
      <ConfirmDialog isOpen={confirming === 'remove'} title={`Remove ${published?.name ?? 'this policy'}?`} confirmLabel="Remove" danger onConfirm={remove} onCancel={() => setConfirming(undefined)}>
        <p>
          {published?.projectIds.length
            ? `Its ${published.projectIds.length === 1 ? 'project follows' : `${published.projectIds.length} projects follow`} the organisation default from their next check-in.`
            : 'It is the organisation default: projects without a policy of their own get none from the platform, and their workbenches go back to their own policy file at their next check-in.'}{' '}
          Other policies stay as they are.
        </p>
      </ConfirmDialog>
    </PageColumn>
  );
}

/** How many things differ from what is published: the name, who it is for, and each section of the document. */
function countChanges(a: Draft, b: Draft): number {
  let n = 0;
  if (a.name.trim() !== b.name.trim()) n++;
  if ([...a.projectIds].sort().join(',') !== [...b.projectIds].sort().join(',')) n++;
  const x = parseOr(a.json);
  const y = parseOr(b.json);
  for (const k of new Set([...Object.keys(x), ...Object.keys(y)])) if (JSON.stringify(x[k]) !== JSON.stringify(y[k])) n++;
  return n;
}

/** Who a policy is for: the whole organisation, or the projects chosen (searched for by name, never listed whole). */
function AppliesTo({ value, onChange, defaultTaken }: { value: string[]; onChange: (ids: string[]) => void; defaultTaken: boolean }) {
  const [some, setSome] = useState(value.length > 0 || defaultTaken);
  const scoped = some || value.length > 0;
  return (
    <ListRow
      title="Applies to"
      description={scoped ? (value.length ? `${value.length} ${value.length === 1 ? 'project' : 'projects'}; everything else follows the organisation default.` : 'Search for the projects this policy is for.') : 'Every project that has no policy of its own.'}
      control={
        <Segment
          aria-label="Applies to"
          size="sm"
          selectedKey={scoped ? 'some' : 'all'}
          onSelectionChange={(k) => {
            setSome(k === 'some');
            if (k === 'all') onChange([]);
          }}
          className="w-fit max-w-full"
        >
          <Segment.Item id="all">The whole organisation</Segment.Item>
          <Segment.Item id="some">Chosen projects</Segment.Item>
        </Segment>
      }
    >
      {scoped && (
        <div className="grid gap-3 rounded-lg bg-surface-secondary p-3">
          <ProjectChips ids={value} onRemove={(id) => onChange(value.filter((x) => x !== id))} />
          <ProjectPicker exclude={value} onPick={(p) => onChange([...value, p.id])} />
        </div>
      )}
    </ListRow>
  );
}
